import React from 'react';
import { Linkedin, Youtube, ArrowUpRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { usePortfolio } from '../context/PortfolioContext';

const ContactPage = () => {
  const { contact, loading } = usePortfolio();

  const socialIcons = {
    linkedin: Linkedin,
    youtube: Youtube
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    hover: {
      scale: 1.05,
      y: -10,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  };

  if (loading || !contact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" data-testid="contact-page">
      <SEO 
        title="Contact | Mir Shahrukh Islam"
        description="Connect with Mir Shahrukh Islam on LinkedIn and YouTube. Interested in collaboration or speaking opportunities."
        url="/contact"
      />
      <Header />
      
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/3 to-purple-500/3 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-24">
        <motion.div 
          className="max-w-4xl mx-auto px-6 text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-sm text-gray-300">
              <Sparkles size={14} className="text-blue-400" />
              Let&apos;s Connect
            </span>
          </motion.div>

          <motion.h1 
            variants={itemVariants}
            className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight"
          >
            Get in Touch
          </motion.h1>

          <motion.p 
            variants={itemVariants}
            className="text-xl text-gray-400 max-w-2xl mx-auto mb-16 leading-relaxed"
          >
            Interested in collaboration, speaking opportunities, or just want to connect? 
            Find me on these platforms.
          </motion.p>

          {/* Social Links Grid */}
          <motion.div 
            variants={containerVariants}
            className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto"
          >
            {contact.social.map((social, index) => {
              const Icon = socialIcons[social.icon];
              return (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={cardVariants}
                  whileHover="hover"
                  className="group relative"
                  data-testid={`social-link-${social.icon}`}
                >
                  {/* Card Background */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/10 p-8 transition-all duration-500 group-hover:border-white/20 group-hover:shadow-2xl group-hover:shadow-blue-500/10">
                    {/* Hover Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* Content */}
                    <div className="relative z-10 flex flex-col items-center gap-4">
                      <div className="p-4 bg-white/10 rounded-xl group-hover:bg-white/20 transition-colors duration-300">
                        <Icon size={32} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-1">{social.platform}</h3>
                        <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                          Follow for updates
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-400 group-hover:text-white transition-colors">
                        <span>Visit</span>
                        <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </motion.div>

          {/* Decorative Element - Removed @islam as requested */}
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
